import type { Messages } from './messages';

/**
 * Copy for `src/app/tenant/members/`. Owned by that page's author, and by nobody else.
 *
 * Every key starts with `tenantMembers.` — the compiler refuses anything else, so
 * this file cannot collide with another page's. Shared words (save, cancel,
 * retry, statuses, role names) are already in `common.ts` and
 * `vocabulary.ts`; use those rather than restating them here.
 */
export const tenantMembers = {
  'tenantMembers.title': 'الأعضاء',
  'tenantMembers.sections': 'أقسام الأعضاء',
  'tenantMembers.sectionAccounts': 'الحسابات',
  'tenantMembers.sectionJoins': 'طلبات الانضمام',

  /* Standing notices */
  'tenantMembers.readOnly': 'رتبتك تتيح العرض فقط. الإضافة وتغيير الأدوار والإيقاف تحتاج صلاحية «تعديل المنشآت».',
  'tenantMembers.tenantSuspended':
    'المنشأة موقوفة: لا يستطيع أحد من أعضائها الدخول حتى تُفعَّل، بمن فيهم من تُرسل إليه دعوة الآن.',
  'tenantMembers.audited': 'يُسجَّل كل إجراء هنا في سجل تدقيق المنصّة باسمك.',
  'tenantMembers.noContent': 'الأسماء والأدوار والنشاط فقط — لا يُقرأ من هنا شيء مما كتبه الأعضاء.',

  /* Accounts table */
  'tenantMembers.accountsCaption': 'حسابات المنشأة',
    'tenantMembers.add': 'إضافة عضو',
  'tenantMembers.search': 'ابحث بالاسم أو البريد',
  'tenantMembers.statusFilter': 'تصفية حسب الحالة',
  'tenantMembers.filterAll': 'الكل',
  'tenantMembers.columnMember': 'العضو',
  'tenantMembers.columnRole': 'الدور',
  'tenantMembers.columnStatus': 'الحالة',
  'tenantMembers.columnLastSignIn': 'آخر دخول',
  'tenantMembers.lastSeen': 'آخر نشاط {time}',
  'tenantMembers.columnCreated': 'أُنشئ',
  'tenantMembers.columnActions': 'إجراءات العضو',
  'tenantMembers.mustChangePassword': 'لم يغيّر كلمة المرور الأولى',
  'tenantMembers.reviewer': 'يراجع المحادثات',
  'tenantMembers.lastAdmin': 'آخر مشرف عام نشط',
  'tenantMembers.invitationExpires': 'تنتهي الدعوة {date}',
  'tenantMembers.invitationLabel': 'الدعوة',
  'tenantMembers.invitationExpired': 'انتهت',
  'tenantMembers.newLink': 'إرسال رابط جديد',
  'tenantMembers.newLinkFor': 'إرسال رابط جديد إلى {name}',
  'tenantMembers.emptyTitle': 'لا حسابات في هذه المنشأة بعد',
  'tenantMembers.emptyBody': 'أضف أول عضو وستُرسل إليه دعوة لاختيار كلمة المرور.',
  'tenantMembers.emptyBodyReadOnly': 'لا حسابات بعد. إضافة الأعضاء تحتاج صلاحية «تعديل المنشآت».',
  'tenantMembers.noMatchTitle': 'لا أعضاء يطابقون البحث والتصفية',
  'tenantMembers.noMatchBody': 'جرّب جزءًا من الاسم أو البريد، أو اختر «الكل».',
  'tenantMembers.clearFilters': 'مسح البحث والتصفية',
  'tenantMembers.noAdmin': 'لا مشرف عام نشط في هذه المنشأة: لا أحد فيها يستطيع إدارة أعضائها. رقِّ عضوًا نشطًا إلى «المشرف العام».',

  /* Row menu */
  'tenantMembers.menuFor': 'إجراءات {name}',
  'tenantMembers.resend': 'إعادة إرسال الدعوة',
  'tenantMembers.resendWait': 'متاحة بعد {seconds} ث',
  'tenantMembers.changeRole': 'تغيير الدور',
  'tenantMembers.suspend': 'إيقاف الحساب',
  'tenantMembers.reactivate': 'إعادة التفعيل',
  'tenantMembers.decideJoin': 'البتّ في طلب الانضمام',

  /* Create */
  'tenantMembers.createTitle': 'إضافة عضو إلى المنشأة',
  'tenantMembers.createDescription': 'يُنشأ الحساب بحالة «بانتظار التفعيل» وتُرسل الدعوة في الطلب نفسه.',
  'tenantMembers.email': 'البريد الإلكتروني',
  'tenantMembers.emailHint': 'إليه تُرسل الدعوة، وبه يدخل العضو.',
  'tenantMembers.name': 'الاسم',
  'tenantMembers.nameHint': 'كما يظهر لزملائه في المنشأة.',
  'tenantMembers.role': 'الدور',
  'tenantMembers.roleHint': 'ابدأ بأضيق دور يكفي؛ رفعه لاحقًا أسهل من ملاحظة صلاحية زائدة.',
  'tenantMembers.create': 'إنشاء وإرسال الدعوة',
  'tenantMembers.emailRequired': 'اكتب بريد العضو.',
  'tenantMembers.emailInvalid': 'هذا لا يبدو عنوان بريد صالحًا.',
  'tenantMembers.operatorAddress': 'هذا العنوان لمشغّل في المنصّة ولا يمكن أن يكون حساب عضو في منشأة.',
  'tenantMembers.nameRequired': 'اكتب اسم العضو.',
  'tenantMembers.createFailedHint':
    'إن كان الحساب قد أُنشئ قبل الخطأ فسيظهر في القائمة، وإعادة المحاولة بالبريد نفسه ترسل الدعوة دون تكرار الحساب.',
  'tenantMembers.createdTitle': 'أُنشئ الحساب ووُضعت الدعوة في طابور البريد',
  'tenantMembers.createdBody':
    'الحساب الآن «بانتظار التفعيل». الدعوة إلى {email} في طابور الإرسال، و«في الطابور» لا يعني أنها وصلت: ما جرى لها بعد ذلك يظهر في سجل الإرسال في تبويب البريد.',
  'tenantMembers.createdExpires': 'ينتهي رابط الدعوة {expires}. بعده يحتاج العضو رابطًا جديدًا من قائمة الأعضاء.',
  'tenantMembers.openMailLog': 'سجل الإرسال',
  'tenantMembers.addAnother': 'إضافة عضو آخر',

  /* Resend */
  'tenantMembers.resendTitle': 'إعادة إرسال الدعوة إلى {name}',
  'tenantMembers.resendConsequence':
    'يُصدر رابط جديد لاختيار كلمة المرور ويُرسل إلى بريده، ويتوقف أي رابط سابق فورًا — فإن كان يحاول الدخول برسالة قديمة فسيحتاج الجديدة.',
  'tenantMembers.resendConfirm': 'إرسال رابط جديد',
  'tenantMembers.resent': 'وُضعت الدعوة إلى {email} في طابور البريد، وينتهي رابطها {expires}. الرابط السابق لم يعد يعمل.',
  'tenantMembers.resentNotice': 'وُضعت الدعوة إلى {email} في طابور البريد، وينتهي رابطها {expires}. تابع وصولها في سجل الإرسال.',

  /* Role */
  'tenantMembers.roleTitle': 'تغيير دور {name}',
  'tenantMembers.roleDescription': 'يسري الدور الجديد في طلبه التالي. لا تُرسل إليه رسالة.',
  'tenantMembers.roleCurrent': 'الحالي',
  'tenantMembers.roleSave': 'تغيير الدور',
  'tenantMembers.roleChanged': 'أصبح دور {name}: {role}.',
  'tenantMembers.roleUnchanged': 'اختر دورًا غير دوره الحالي.',
  'tenantMembers.lastAdminDemote':
    '{name} آخر مشرف عام نشط في المنشأة. ستُرفض إزالته من الدور لأن المنشأة ستبقى بلا من يديرها — رقِّ عضوًا آخر إلى «المشرف العام» أولًا.',

  /* Suspend / reactivate */
  'tenantMembers.suspendTitle': 'إيقاف حساب {name}',
  'tenantMembers.suspendConsequence':
    'تُرفض كل جلساته المفتوحة من طلبه التالي، ولا يستطيع الدخول حتى يُعاد تفعيله. لا يُحذف شيء من حسابه أو محتواه، ويبقى دوره كما هو.',
  'tenantMembers.lastAdminSuspend':
    '{name} آخر مشرف عام نشط في المنشأة. سيُرفض الإيقاف لأن المنشأة ستبقى بلا من يديرها — رقِّ عضوًا آخر إلى «المشرف العام» أولًا.',
  'tenantMembers.suspendConfirm': 'إيقاف الحساب',
  'tenantMembers.suspended': 'أُوقف حساب {name}.',
  'tenantMembers.reactivateTitle': 'إعادة تفعيل {name}',
  'tenantMembers.reactivateConsequence': 'يستطيع الدخول من جديد بدوره الحالي ({role}). لا تُرسل إليه رسالة.',
  'tenantMembers.reactivateInvited': 'لم يدخل قط. إن لم يكن قد اختار كلمة مرور فأعد إرسال الدعوة بعد التفعيل ليتمكّن من الدخول.',
  'tenantMembers.reactivateConfirm': 'إعادة التفعيل',
  'tenantMembers.reactivated': 'أُعيد تفعيل حساب {name}.',
  'tenantMembers.affected': '{name} · {email}',

  /* Join requests */
  'tenantMembers.joinsCaption': 'طلبات الانضمام إلى المنشأة',
  'tenantMembers.joinsFilter': 'حالة الطلب',
  'tenantMembers.joinsIntro':
    'أشخاص دخلوا عبر دليل جهة عملهم ولم يصبحوا أعضاء بعد. القبول يحتاج دورًا، والرفض لا يحمل سببًا.',
  'tenantMembers.columnRequester': 'مقدّم الطلب',
  'tenantMembers.columnRequested': 'طلب في',
  'tenantMembers.columnReviewed': 'بُتّ فيه',
  'tenantMembers.columnDecision': 'القرار',
  'tenantMembers.approve': 'قبول',
  'tenantMembers.reject': 'رفض',
  'tenantMembers.joinsEmptyPending': 'لا طلبات انضمام بانتظار البتّ',
  'tenantMembers.joinsEmptyPendingBody': 'حين يدخل أحد عبر دليل جهة العمل دون أن يكون عضوًا يظهر طلبه هنا.',
  'tenantMembers.joinsEmptyApproved': 'لم يُقبل أي طلب بعد',
  'tenantMembers.joinsEmptyRejected': 'لم يُرفض أي طلب',
  'tenantMembers.joinsEmptyDecidedBody': 'الطلبات المبتوت فيها تبقى هنا للرجوع إليها.',
  'tenantMembers.approveTitle': 'قبول {name}',
  'tenantMembers.approveDescription': 'يصبح الحساب نشطًا بالدور الذي تختاره، ويدخل في محاولته التالية. لا تُرسل إليه رسالة من هنا.',
  'tenantMembers.approveRole': 'الدور عند القبول',
  'tenantMembers.approveRoleHint': 'لا قبول بلا دور: من يدخل دون دور لا يستطيع فعل شيء.',
  'tenantMembers.approveConfirm': 'قبول بهذا الدور',
  'tenantMembers.approved': 'قُبل {name} بدور {role}.',
  'tenantMembers.rejectTitle': 'رفض طلب {name}',
  'tenantMembers.rejectConsequence':
    'لا يحصل على أي وصول إلى المنشأة. يبقى الطلب مسجّلًا «مرفوضًا»، فإن حاول الدخول مجددًا أُبلغ بالقرار بدل فتح طلب جديد. لا تُرسل إليه رسالة من هنا.',
  'tenantMembers.rejectNoReason': 'لا يُرفق سبب بالرفض: الواجهة لا تحمل سببًا قصدًا، لأن ما يُكتب هنا يقرؤه مقدّم الطلب.',
  'tenantMembers.rejectConfirm': 'رفض الطلب',
  'tenantMembers.rejected': 'رُفض طلب {name}.',
  'tenantMembers.raceLost': 'بتّ أحد آخر في هذا الطلب قبلك. حُدّثت القائمة.',
} as const satisfies Messages<'tenantMembers'>;
